/**
 * Die Probezeit-Zeile einer Person für die Personenfenster
 * (`GET /api/probezeit/:personioId`, Spezifikation 2026-09-16, R7).
 *
 * Der Baustein „Wirtschaftlichkeit in der Probezeit" und die Beschriftung der
 * Krankheits-Kacheln fragen über denselben Schlüssel; React Query schickt die Anfrage
 * deshalb nur einmal. Ob jemand in der Probezeit ist, entscheidet allein der Server,
 * mit derselben Regel wie für die Liste „In der Probezeit". Was er antwortet:
 *
 * - 200: die Person mit ihren Zahlen.
 * - 404, „nicht in der Probezeit": `null`. Das ist der häufige Fall und eine normale Antwort,
 *   kein Fehler. Als Fehler wäre er teuer: Eine Abfrage im Fehlerstand hat keine Daten und gilt
 *   darum immer als veraltet. Sie fragt bei jedem Einhängen neu (das Mitarbeiterfenster hängt
 *   den Block beim Reiterwechsel ab und wieder an) und bei jedem Fensterfokus, und jede Anfrage
 *   rechnet auf dem Server die ganze Belegschaft. `null` ist dagegen Daten wie jede andere und
 *   gilt für die Frischezeit der Anwendung (`staleTime`).
 * - 403 (die Rolle sieht die Zahlen nicht) und jeder andere Fehler: bleiben Fehler. Ohne Daten
 *   werden sie beim nächsten Einhängen oder Fensterfokus neu gefragt, ein kurzer Serverfehler
 *   heilt sich so von selbst. Darum schaltet hier nichts dieses erneute Fragen ab
 *   (`retryOnMount`, `refetchOnWindowFocus`): Ein einmaliger Serverfehler bliebe sonst für die
 *   Lebensdauer der Abfrage im Zwischenspeicher hängen.
 */
import { useQuery } from "@tanstack/react-query";
import { type ApiFehler, type ProbezeitPerson, probezeitApi } from "../lib/api.js";

/** Die Person, oder `null`, wenn der Server sie nicht in der Probezeit führt (404). */
async function holePerson(personioId: number): Promise<ProbezeitPerson | null> {
  try {
    return await probezeitApi.getPerson(personioId);
  } catch (fehler) {
    // `request` hängt den Statuscode der Antwort an den Fehler; bei einem Netzfehler fehlt er.
    if ((fehler as ApiFehler).status === 404) return null;
    throw fehler;
  }
}

export function useProbezeitPerson(personioId: number | null) {
  return useQuery<ProbezeitPerson | null, ApiFehler>({
    queryKey: ["probezeit", personioId],
    queryFn: () => holePerson(personioId!),
    enabled: personioId !== null,
    // Ein Fehler wird nicht sofort wiederholt: Jeder Versuch rechnet auf dem Server die ganze
    // Belegschaft, und eine Rolle ohne Zugriff (403) ändert sich nicht von selbst. Neu gefragt
    // wird beim nächsten Einhängen oder Fensterfokus.
    retry: false,
  });
}

/**
 * Läuft die Probezeit? Dann heißen die Krankheits-Kacheln „seit Eintritt": In der
 * Probezeit sind 12 Monate derselbe Zeitraum. Wahr nur, wenn der Server die Person in der
 * Probezeit führt und sie schon eingetreten ist (`lage` ist „laeuft"). Für jemanden, der erst
 * eintritt (`lage` ist „kuenftig"), liefert der Server auch eine Zeile, aber „seit Eintritt"
 * nennt dort einen Zeitraum, der noch nicht begonnen hat. In jedem anderen Fall bleibt es bei
 * der bisherigen Beschriftung: solange die Antwort fehlt, bei „nicht in der Probezeit" und bei
 * jedem Fehler.
 *
 * Ohne Person (`personioId` ist `null`) fragt der Hook nicht. Die Fenster übergeben die Person
 * erst, wenn ihr Profil da ist. Für die Zahl der Anfragen ist die Reihenfolge von Hook und Block
 * ohne Belang: Jede Antwort, auch „nicht in der Probezeit", liegt danach im Zwischenspeicher,
 * und wer später einhängt, findet sie dort.
 */
export function useInProbezeit(personioId: number | null): boolean {
  // `null` = nicht in der Probezeit, `undefined` = noch keine Antwort oder ein Fehler. Beides ist
  // hier falsch, ebenso eine Person, die erst eintritt (`lage` ist „kuenftig").
  return useProbezeitPerson(personioId).data?.lage === "laeuft";
}
